"use client"

import React, { useState, ChangeEvent, FormEvent, useEffect } from 'react';
import styles from './TrackApplication.module.css';
import { TrackApplicationResponse } from '@/models/api/trackApplication';
import {
  trackApplicationStatus,
  sendTrackApplicationOtp,
  verifyTrackApplicationOtp,
  formatDateForApi,
  formatDateForDisplay
} from '@/api/trackApplicationService';
import { useRouter } from "next/router";
import { formatDate } from '../ui/formateFunction';
import {
  FiCheckCircle,
  FiClock,
  FiAlertCircle,
  FiUser,
  FiCalendar,
  FiArrowRight,
  FiLock,
  FiMail,
  FiPhone,
  FiFlag,
  FiInfo,
  FiCheck,
  FiChevronRight,
  FiHeadphones
} from 'react-icons/fi';
import { FaRegLightbulb } from 'react-icons/fa';

interface ApplicationStep {
  id: string;
  label: string;
  status: 'completed' | 'in-progress' | 'pending' | 'blocked';
  date?: string;
  description?: string;
  icon: React.ReactNode;
  actionLabel?: string;
  actionUrl?: string;
}

// Verified-OTP session: once a code is verified, the token this endpoint issued is cached here
// for 5 minutes so a page refresh (or re-submitting the same email + DOB) doesn't re-prompt for
// OTP. The token itself is still re-checked by the backend on every track-status call - this
// cache only spares the user from re-entering a code, it isn't what grants access.
const OTP_SESSION_KEY = 'otpVerificationSession';
const OTP_SESSION_TTL_MS = 5 * 60 * 1000;
const OTP_RESEND_COOLDOWN_SECONDS = 60;

interface OtpSessionCache {
  studentEmail: string;
  dobApi: string;
  verificationToken: string;
  expiresAt: number;
}

const readOtpSessionCache = (): OtpSessionCache | null => {
  try {
    const raw = localStorage.getItem(OTP_SESSION_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw) as OtpSessionCache;
    if (!cached?.verificationToken || !cached?.expiresAt || Date.now() >= cached.expiresAt) {
      localStorage.removeItem(OTP_SESSION_KEY);
      return null;
    }
    return cached;
  } catch {
    localStorage.removeItem(OTP_SESSION_KEY);
    return null;
  }
};

const TrackStatus: React.FC = () => {
  const [studentEmail, setStudentEmail] = useState<string>('');
  const [dob, setDob] = useState<string>('');
  const [status, setStatus] = useState<TrackApplicationResponse | null>(null);
  const [offerAcceptanceStatusId, setOfferAcceptanceStatusId] = useState<string | null | undefined>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // OTP verification step - shown after the email/DOB form is submitted and before the tracked
  // status is fetched, unless a still-valid verification token is already cached.
  const [otpStage, setOtpStage] = useState<'form' | 'otp'>('form');
  const [pendingEmail, setPendingEmail] = useState('');
  const [pendingDobApi, setPendingDobApi] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const router = useRouter();
  const TTL_30_MINUTES = 30 * 60 * 1000;

  // Restore a still-valid OTP session on mount so a refresh within 5 minutes goes straight back
  // to the tracked status instead of asking for email/DOB and a fresh OTP.
  useEffect(() => {
    const cached = readOtpSessionCache();
    if (!cached) return;

    setStudentEmail(cached.studentEmail);
    setDob(formatDateForDisplay(cached.dobApi));
    setLoading(true);

    trackApplicationStatus(cached.studentEmail, cached.dobApi, cached.verificationToken)
      .then((enquiry) => {
        setStatus(enquiry);
        setOfferAcceptanceStatusId(enquiry?.application?.offerAcceptanceStatusId ?? null);
        setStudentEmail('');
        setDob('');
      })
      .catch(() => {
        // Cached token no longer valid server-side (e.g. already expired) - fall back to a
        // normal form, the next submit will trigger a fresh OTP.
        localStorage.removeItem(OTP_SESSION_KEY);
      })
      .finally(() => setLoading(false));
  }, []);

  // Cooldown ticks down once a second while > 0; resend is disabled until it hits 0.
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const fetchStatusWithToken = async (email: string, dobApi: string, token: string) => {
    setLoading(true);
    setError(null);

    try {
      const enquiry = await trackApplicationStatus(email, dobApi, token);

      setStatus(enquiry);
      setOfferAcceptanceStatusId(enquiry?.application?.offerAcceptanceStatusId ?? null);

      localStorage.setItem(OTP_SESSION_KEY, JSON.stringify({
        studentEmail: email,
        dobApi,
        verificationToken: token,
        expiresAt: Date.now() + OTP_SESSION_TTL_MS,
      } as OtpSessionCache));

      const cacheData = {
        enquiryNumber: enquiry.enquiryNumber,
        studentEmail: email,
        dateOfBirth: dobApi,
        createdAt: Date.now(),
        expiresAt: Date.now() + TTL_30_MINUTES,
      };
      localStorage.setItem("enquirySession", JSON.stringify(cacheData));

      setOtpStage('form');
      setOtpCode('');
      setStudentEmail('');
      setDob('');
    } catch (err: any) {
      setError(err?.message || "Failed to fetch application status.");
      // The verification token turned out to be no good (expired/invalid) - don't leave the
      // user stuck on a dead cached session, send them back to re-request an OTP.
      localStorage.removeItem(OTP_SESSION_KEY);
      setOtpStage('form');
    } finally {
      setLoading(false);
    }
  };

  const handleTrackSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!studentEmail || !dob) {
      setError('Please fill in all required fields');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(studentEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    const dobRegex = /^\d{2}\s*\/\s*\d{2}\s*\/\s*\d{4}$/;
    if (!dobRegex.test(dob)) {
      setError('Please enter a valid date of birth (MM / DD / YYYY)');
      return;
    }

    const formattedDob = formatDateForApi(dob);
    const trimmedEmail = studentEmail.trim();
    setError(null);
    setStatus(null);

    // A still-valid token for this exact email + DOB means this pair was already verified
    // within the last 5 minutes - go straight to fetching the status, no OTP needed.
    const cached = readOtpSessionCache();
    if (
      cached &&
      cached.studentEmail.toLowerCase() === trimmedEmail.toLowerCase() &&
      cached.dobApi === formattedDob
    ) {
      await fetchStatusWithToken(trimmedEmail, formattedDob, cached.verificationToken);
      return;
    }

    setLoading(true);
    try {
      await sendTrackApplicationOtp(trimmedEmail, formattedDob);
      setPendingEmail(trimmedEmail);
      setPendingDobApi(formattedDob);
      setOtpCode('');
      setOtpError(null);
      setOtpStage('otp');
      setResendCooldown(OTP_RESEND_COOLDOWN_SECONDS);
    } catch (err: any) {
      setError(err?.message || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: FormEvent) => {
    e.preventDefault();

    if (!/^\d{6}$/.test(otpCode.trim())) {
      setOtpError('Please enter the 6-digit code sent to your email.');
      return;
    }

    setVerifying(true);
    setOtpError(null);

    try {
      const result = await verifyTrackApplicationOtp(pendingEmail, pendingDobApi, otpCode.trim());

      if (!result.verificationToken) {
        setOtpError(result.message || 'Verification failed. Please try again.');
        return;
      }

      await fetchStatusWithToken(pendingEmail, pendingDobApi, result.verificationToken);
    } catch (err: any) {
      setOtpError(err?.message || 'Failed to verify OTP.');
    } finally {
      setVerifying(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;

    setOtpError(null);
    try {
      await sendTrackApplicationOtp(pendingEmail, pendingDobApi);
      setOtpCode('');
      setResendCooldown(OTP_RESEND_COOLDOWN_SECONDS);
    } catch (err: any) {
      setOtpError(err?.message || 'Failed to resend OTP.');
    }
  };

  const handleCancelOtp = () => {
    setOtpStage('form');
    setOtpCode('');
    setOtpError(null);
  };

  const fullName = [status?.studentProfile?.firstName, status?.studentProfile?.lastName]
    .filter(Boolean)
    .join(" ");

  /* Pre-compute shared status values */
  const enquiryStatusCode = status?.enquiryStatusCode?.toUpperCase();
  const applicationStatusCode = status?.application?.applicationStatusCode?.toUpperCase();
  const offerAccepted = Boolean(offerAcceptanceStatusId);

  // Fallback to single invoice if invoices array is not available in the response
  const invoicesList = status?.invoices || (status?.invoice ? [status.invoice] : []);

  const isEnquiryPaid = invoicesList.some(inv => {
    const desc = inv.description?.toLowerCase() || "";
    return inv.isPaid && (desc.includes("application") || desc.includes("admission"));
  });

  const isApplicationPaid = status?.application?.isPaid || invoicesList.some(inv => {
    const desc = inv.description?.toLowerCase() || "";
    return inv.isPaid && desc.includes("placement");
  });

  /* Get current status label */
  const getCurrentStatusLabel = (): string => {
    if (!status) return "";

    if (applicationStatusCode === "ENROLLED") return "Enrolled - Admission Complete";
    if (applicationStatusCode === "OFFERED") {
      if (offerAccepted) {
        return isApplicationPaid ? "Placement Fee Paid / Awaiting Enrollment" : "Offer Accepted / Awaiting Placement Fee";
      }
      return "Offer Received / Action Required";
    }
    if (applicationStatusCode === "PENDING" || applicationStatusCode === "SUBMITTED") {
      return isEnquiryPaid ? "Application Submitted & Paid" : "Application Under Review";
    }
    if (enquiryStatusCode === "APPROVED") return "Enquiry Approved";
    if (enquiryStatusCode === "REJECTED") return "Enquiry Rejected";
    if (enquiryStatusCode === "REVIEWED") return "Enquiry Under Review";
    if (enquiryStatusCode === "SUBMITTED") {
      return isEnquiryPaid ? "Enquiry Submitted" : "Payment Pending";
    }

    return "Pending";
  };

  /* Get computed next action label */
  const getNextActionLabel = (): string => {
    if (!status) return "";

    if (applicationStatusCode === "ENROLLED") return "None (Completed)";
    if (applicationStatusCode === "OFFERED") {
      if (offerAccepted) {
        return isApplicationPaid ? "Awaiting Enrollment" : "Placement Fee Payment";
      }
      return "Accept Offer";
    }
    if (applicationStatusCode && !isEnquiryPaid) return "Complete Payment";
    if (enquiryStatusCode === "SUBMITTED" && !isEnquiryPaid) return "Pay Enquiry Fee";

    return "Awaiting Review";
  };

  /* Build application steps based on actual status */
  const getApplicationSteps = (): ApplicationStep[] => {
    if (!status) return [];

    const enquiryDate = status?.application?.createdDate;
    const interviewDate = status?.application?.interviewDate;
    const modifiedDate = status?.application?.modifiedDate;

    const steps: ApplicationStep[] = [
      {
        id: 'enquiry-submitted',
        label: 'Enquiry Submitted',
        status: 'completed',
        date: enquiryDate ? formatDate(enquiryDate) : undefined,
        description: `Application #${status.enquiryNumber}`,
        icon: <FiCheckCircle size={20} />,
        actionLabel: enquiryStatusCode === 'SUBMITTED' && !isEnquiryPaid ? 'Pay Enquiry Fee' : undefined,
        actionUrl: enquiryStatusCode === 'SUBMITTED' && !isEnquiryPaid ? '/admission/payment' : undefined,
      },
      {
        id: 'application-payment',
        label: 'Application Payment',
        status: isEnquiryPaid ? 'completed' :
          applicationStatusCode && !isEnquiryPaid ? 'in-progress' : 'pending',
        date: isEnquiryPaid ? (modifiedDate ? formatDate(modifiedDate) : undefined) : undefined,
        description: isEnquiryPaid ? 'Payment completed' :
          applicationStatusCode && !isEnquiryPaid ? '⏳ Payment pending' : undefined,
        icon: isEnquiryPaid ? <FiCheckCircle size={20} /> :
          applicationStatusCode && !isEnquiryPaid ? <FiAlertCircle size={20} /> : <FiClock size={20} />,
        actionLabel: applicationStatusCode && !isEnquiryPaid ? 'Complete Payment' : undefined,
        actionUrl: applicationStatusCode && !isEnquiryPaid ? '/admission/application/payment' : undefined,
      },
      {
        id: 'campus-visit',
        label: 'Campus Visit Scheduled',
        status: interviewDate ? 'completed' :
          isEnquiryPaid ? 'in-progress' : 'pending',
        date: interviewDate ? formatDate(interviewDate) : undefined,
        description: isEnquiryPaid && !interviewDate ? '⏳ Awaiting visit scheduling' : undefined,
        icon: interviewDate ? <FiCheckCircle size={20} /> : <FiClock size={20} />,
      },
      {
        id: 'visit-outcome',
        label: 'Visit Outcome & Decision',
        status: ['OFFERED', 'ENROLLED', 'ONHOLD', 'REJECT', 'WAITLIST'].includes(applicationStatusCode || '') ? 'completed' :
          interviewDate ? 'in-progress' : 'pending',
        description: applicationStatusCode === 'OFFERED' ? 'Offer Extended' :
          applicationStatusCode === 'REJECT' ? 'Application Rejected' :
            applicationStatusCode === 'WAITLIST' ? 'Waitlisted' :
              applicationStatusCode === 'ONHOLD' ? 'On Hold' : undefined,
        icon: ['OFFERED', 'ENROLLED', 'ONHOLD', 'REJECT', 'WAITLIST'].includes(applicationStatusCode || '') ? <FiCheckCircle size={20} /> : <FiClock size={20} />,
      },
      {
        id: 'offer-acceptance',
        label: 'Offer Acceptance',
        status: offerAccepted ? 'completed' :
          applicationStatusCode === 'OFFERED' ? 'in-progress' : 'pending',
        description: offerAccepted ? 'Offer Accepted' :
          applicationStatusCode === 'OFFERED' ? 'Awaiting your decision' : undefined,
        icon: offerAccepted ? <FiCheckCircle size={20} /> :
          applicationStatusCode === 'OFFERED' ? <FiAlertCircle size={20} /> : <FiClock size={20} />,
        actionLabel: applicationStatusCode === 'OFFERED' && !offerAccepted ? 'Accept Offer' : undefined,
        actionUrl: applicationStatusCode === 'OFFERED' && !offerAccepted ? '/offer' : undefined,
      },
      {
        id: 'offer-payment',
        label: 'Placement Fee Payment',
        status: isApplicationPaid || applicationStatusCode === 'ENROLLED' ? 'completed' :
          applicationStatusCode === 'OFFERED' && offerAccepted && !isApplicationPaid ? 'in-progress' : 'pending',
        description: isApplicationPaid ? 'Payment completed' :
          applicationStatusCode === 'OFFERED' && offerAccepted && !isApplicationPaid ? 'Payment pending' : undefined,
        icon: isApplicationPaid || applicationStatusCode === 'ENROLLED' ? <FiCheckCircle size={20} /> :
          applicationStatusCode === 'OFFERED' && offerAccepted && !isApplicationPaid ? <FiAlertCircle size={20} /> : <FiClock size={20} />,
        actionLabel: applicationStatusCode === 'OFFERED' && offerAccepted && !isApplicationPaid ? 'Complete Payment' : undefined,
        actionUrl: applicationStatusCode === 'OFFERED' && offerAccepted && !isApplicationPaid ? '/placement/payment' : undefined,
      },
      {
        id: 'enrolled',
        label: 'Enrolled - Admission Complete',
        status: applicationStatusCode === 'ENROLLED' ? 'completed' : 'pending',
        date: applicationStatusCode === 'ENROLLED' ? (modifiedDate ? formatDate(modifiedDate) : undefined) : undefined,
        icon: applicationStatusCode === 'ENROLLED' ? <FiCheckCircle size={20} /> : <FiClock size={20} />,
      },
    ];

    return steps;
  };

  /* Handle action button click - navigate to proper page */
  const handleAction = (url: string | undefined) => {
    if (url) {
      const existingSessionStr = localStorage.getItem("enquirySession");
      if (existingSessionStr) {
        try {
          const sessionData = JSON.parse(existingSessionStr);
          sessionData.expiresAt = Date.now() + TTL_30_MINUTES;
          localStorage.setItem("enquirySession", JSON.stringify(sessionData));
        } catch (e) {
          console.error("Failed to parse existing session");
        }
      }
      router.push(url);
    }
  };

  // Format DOB input as user types (MM/DD/YYYY)
  const handleDobChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const cleaned = value.replace(/\D/g, '');

    if (cleaned.length <= 2) {
      setDob(cleaned);
    } else if (cleaned.length <= 4) {
      setDob(`${cleaned.slice(0, 2)} / ${cleaned.slice(2)}`);
    } else {
      setDob(`${cleaned.slice(0, 2)} / ${cleaned.slice(2, 4)} / ${cleaned.slice(4, 8)}`);
    }

    if (error) setError(null);
    if (status) setStatus(null);
  };

  const renderStepIndicator = (step: ApplicationStep, index: number, totalSteps: number) => {
    let circleClass = styles.stepCircle;
    let iconElement: React.ReactNode = <span>{index + 1}</span>;

    if (step.status === 'completed') {
      circleClass = `${styles.stepCircle} ${styles.stepCircleCompleted}`;
      iconElement = <FiCheck size={16} />;
    } else if (step.status === 'in-progress') {
      circleClass = `${styles.stepCircle} ${styles.stepCircleActive}`;
      iconElement = <div className={styles.stepCircleActiveInner} />;
    } else {
      circleClass = `${styles.stepCircle} ${styles.stepCirclePending}`;
      iconElement = <span>{index + 1}</span>;
    }

    const lineClass = `${styles.stepVerticalLine} ${step.status === 'completed' ? styles.lineCompleted :
      step.status === 'in-progress' ? styles.lineActive : ''
      }`;

    return (
      <div className={styles.stepIndicatorContainer}>
        <div className={circleClass}>
          {iconElement}
        </div>
        {index < totalSteps - 1 && (
          <div className={lineClass} />
        )}
      </div>
    );
  };

  return (
    <div className={`container ${styles.py100}`}>
      <div className={styles.contentBox}>
        <h1 className={styles.title}>Track Your Application Status</h1>
        <p className={styles.subtitle}>
          Use your registered email address and date of birth to check your admission status.
        </p>

        <div>
          {/* Main Column */}
          <div className={styles.trackingMainColumn}>
            <div className={styles.formContainer}>
              {otpStage === 'form' ? (
                <>
                  <h2 className={styles.formTitle}>
                    <span className={styles.formTitleIcon}>
                      <FiUser size={20} />
                    </span>
                    Track Your Application
                  </h2>
                  <p className={styles.formDescription}>
                    Enter your email address and date of birth to check your application status and continue your journey.
                  </p>

                  <form onSubmit={handleTrackSubmit} className={styles.form}>
                    <div className={styles.twolayoutgrid}>
                      <div className={styles.inputGroup}>
                        <label className={`${styles.label} ${styles.required}`}>
                          Student Email Address
                        </label>
                        <input
                          type="email"
                          value={studentEmail}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            setStudentEmail(e.target.value);
                            if (error) setError(null);
                            if (status) setStatus(null);
                          }}
                          placeholder="e.g. student@example.com"
                          className={styles.input}
                        />
                      </div>

                      <div className={styles.inputGroup}>
                        <label className={`${styles.label} ${styles.required}`}>
                          Date of Birth
                        </label>
                        <div className={styles.inputWrapper}>
                          <input
                            type="text"
                            value={dob}
                            onChange={handleDobChange}
                            placeholder="MM / DD / YYYY"
                            className={styles.input}
                            maxLength={14}
                          />
                          <FiCalendar className={styles.inputIcon} />
                        </div>
                      </div>
                    </div>

                    {error && (
                      <div className={styles.errorBox}>
                        <p className={styles.errorText}>{error}</p>
                      </div>
                    )}

                    <button
                      type="submit"
                      className={`${styles.button} ${styles.buttonPrimary}`}
                      disabled={loading || !!error || !studentEmail || !dob}
                    >
                      {loading ? "Checking..." : "Track Application Status"}
                      <FiArrowRight size={16} />
                    </button>
                  </form>
                </>
              ) : (
                <>
                  <h2 className={styles.formTitle}>
                    <span className={styles.formTitleIcon}>
                      <FiLock size={20} />
                    </span>
                    Enter Verification Code
                  </h2>
                  <p className={styles.formDescription}>
                    We sent a 6-digit code to <strong>{pendingEmail}</strong>. Enter it below to view your application
                    status. The code expires in 5 minutes.
                  </p>

                  <form onSubmit={handleVerifyOtp} className={styles.form}>
                    <div className={styles.inputGroup}>
                      <label className={`${styles.label} ${styles.required}`}>
                        Verification Code
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={otpCode}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                          if (otpError) setOtpError(null);
                        }}
                        placeholder="123456"
                        maxLength={6}
                        className={styles.input}
                        autoFocus
                      />
                    </div>

                    {otpError && (
                      <div className={styles.errorBox}>
                        <p className={styles.errorText}>{otpError}</p>
                      </div>
                    )}

                    <button
                      type="submit"
                      className={`${styles.button} ${styles.buttonPrimary}`}
                      disabled={verifying || otpCode.length !== 6}
                    >
                      {verifying ? "Verifying..." : "Verify & Continue"}
                      <FiArrowRight size={16} />
                    </button>

                    <div className={styles.twolayoutgrid}>
                      <button
                        type="button"
                        className={`${styles.button} ${styles.buttonOutline}`}
                        onClick={handleResendOtp}
                        disabled={resendCooldown > 0}
                      >
                        {resendCooldown > 0 ? `Resend code (${resendCooldown}s)` : "Resend code"}
                      </button>
                      <button
                        type="button"
                        className={`${styles.button} ${styles.buttonOutline}`}
                        onClick={handleCancelOtp}
                      >
                        Use a different email
                      </button>
                    </div>
                  </form>
                </>
              )}

              {/* <div className={styles.helpBox}>
                <FiLock size={16} />
                <p className={styles.helpText}>
                  Use your registered email address and date of birth to view your application status.                </p>
              </div> */}
            </div>


          </div>

          {/* Sidebar Column */}

          <div>
            {status && (
              <>
                {/* Student Info Summary */}
                <div className={styles.studentInfoBox}>
                  <div className={styles.infoItem}>
                    <span className={styles.infoIcon}><FiUser /></span>
                    <div className={styles.infoContent}>
                      <p className={styles.infoLabel}>Student Name</p>
                      <p className={styles.infoValue}>{fullName || 'N/A'}</p>
                    </div>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoIcon}><FiCalendar /></span>
                    <div className={styles.infoContent}>
                      <p className={styles.infoLabel}>Application ID</p>
                      <p className={styles.infoValue}>{status.enquiryNumber}</p>
                    </div>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoIcon}><FiClock /></span>
                    <div className={styles.infoContent}>
                      <p className={styles.infoLabel}>Current Status</p>
                      <span className={styles.statusBadge}>{getCurrentStatusLabel()}</span>
                    </div>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoIcon}><FiFlag /></span>
                    <div className={styles.infoContent}>
                      <p className={styles.infoLabel}>Next Action</p>
                      <p className={styles.infoValue}>{getNextActionLabel()}</p>
                    </div>
                  </div>
                </div>

                {/* Timeline Steps */}
                <div className={styles.trackingLayoutGrid}>
                  <div>
                    <div className={styles.timelineSection}>
                      <div className={styles.timeline}>
                        {getApplicationSteps().map((step, index, array) => {
                          const isActive = step.status === 'in-progress';
                          return (
                            <div
                              key={step.id}
                              className={`${styles.stepRow} ${isActive ? styles.stepActive : ''}`}
                            >
                              <div className={styles.stepLeft}>
                                {renderStepIndicator(step, index, array.length)}
                                <span className={styles.stepNumber}>{index + 1}</span>
                                <div className={styles.stepDetails}>
                                  <h4 className={styles.stepTitle}>{step.label}</h4>
                                  {step.date && (
                                    <p className={`${styles.stepMeta} ${step.status === 'completed' ? styles.stepMetaCompleted : ''}`}>
                                      {step.date}
                                    </p>
                                  )}
                                  {step.description && (
                                    <p className={`${styles.stepMeta} ${isActive ? styles.stepMetaActive : ''}`}>{step.description}</p>
                                  )}
                                </div>
                              </div>

                              <div className={styles.stepRight}>
                                {step.actionLabel && step.actionUrl ? (
                                  <button
                                    className={`${styles.stepActionButton} ${isActive ? styles.stepActionButtonPrimary : styles.stepActionButtonOutline
                                      }`}
                                    onClick={() => handleAction(step.actionUrl)}
                                  >
                                    {step.actionLabel}
                                    {isActive ? <FiArrowRight size={14} /> : <FiChevronRight size={16} />}
                                  </button>
                                ) : step.status === 'completed' ? (
                                  <span className={styles.badgeCompleted}>Completed</span>
                                ) : null}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>


                  </div>
                  <div className={styles.trackingSidebarColumn}>
                    {/* Need Help Card */}
                    <div className={styles.sidebarCard}>
                      <h3 className={styles.sidebarCardTitle}>
                        <FiHeadphones className={styles.sidebarTitleIcon} />
                        Need Help?
                      </h3>
                      <p className={styles.sidebarCardText}>
                        Our admissions team is here to assist you with your application journey.
                      </p>
                      <div className={styles.sidebarList}>
                        {/* <div className={styles.sidebarItem}>
                          <FiMail className={styles.sidebarItemIcon} />
                          <div className={styles.sidebarItemContent}>
                            <span className={styles.sidebarItemLabel}>Email Us</span>
                            <span className={styles.sidebarItemValue}>
                              <a href="mailto:admissions@caymanacademy.edu" className={styles.sidebarItemValueLink}>
                                admissions@caymanacademy.edu
                              </a>
                            </span>
                          </div>
                        </div> */}
                        <div className={styles.sidebarItem}>
                          <FiPhone className={styles.sidebarItemIcon} />
                          <div className={styles.sidebarItemContent}>
                            <span className={styles.sidebarItemLabel}>Call Us</span>
                            <span className={styles.sidebarItemValue}>
                              <a href="tel:+13456402642" className={styles.sidebarItemValueLink}>
                                +1 (345) 640-2642
                              </a>
                            </span>
                          </div>
                        </div>
                        <div className={styles.sidebarItem}>
                          <FiClock className={styles.sidebarItemIcon} />
                          <div className={styles.sidebarItemContent}>
                            <span className={styles.sidebarItemLabel}>Office Hours</span>
                            <span className={styles.sidebarItemValue}>
                              Monday - Thursday : 8:30 AM - 5:00 PM
                              <br />
                              Friday: 8:30 AM - 1:00 PM
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Application Tips Card */}
                    <div className={styles.sidebarCard}>
                      <h3 className={styles.sidebarCardTitle}>
                        <FaRegLightbulb className={styles.sidebarTitleIcon} />
                        Application Tips
                      </h3>
                      <div className={styles.tipList}>
                        <div className={styles.tipItem}>
                          <FiCheck className={styles.tipIcon} />
                          <span>Complete each step to move forward</span>
                        </div>
                        <div className={styles.tipItem}>
                          <FiMail className={styles.tipIcon} />
                          <span>Check your email for updates</span>
                        </div>
                        <div className={styles.tipItem}>
                          <FiAlertCircle className={styles.tipIcon} />
                          <span>Respond promptly to avoid delays</span>
                        </div>
                        <div className={styles.tipItem}>
                          <FiClock className={styles.tipIcon} />
                          <span>Contact us if you need assistance</span>
                        </div>
                      </div>
                    </div>
                    <div className={styles.noteBox}>
                      <FiInfo size={20} style={{ color: 'var(--text-blue)', flexShrink: 0 }} />
                      <p className={styles.noteText}>
                        Each step in your journey has been tracked. Click on any action button above to continue your application or check the next steps required.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>


        </div>
      </div>
    </div>
  );
};

export default TrackStatus;
